import { User } from 'oidc-client-ts';
import type { Problem } from './types';

interface SessionTokenResponse {
  accessToken: string;
  idToken: string;
  tokenType: string;
  expiresIn: number;
  scope: string;
  profile: {
    sub: string;
    username?: string;
    iss: string;
    aud: string;
    exp: number;
    iat: number;
  };
}

export class SessionExpiredError extends Error {}

export async function establishBrowserSession(user: User): Promise<User> {
  if (!user.refresh_token) throw new SessionExpiredError('The refresh token is missing');
  return sessionRequest('/auth/session', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${user.access_token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ refreshToken: user.refresh_token }),
  });
}

export function refreshBrowserSession(): Promise<User> {
  return sessionRequest('/auth/session/refresh', { method: 'POST' });
}

export async function deleteBrowserSession(): Promise<void> {
  const response = await fetch('/auth/session', { method: 'DELETE', credentials: 'same-origin' });
  if (!response.ok && response.status !== 401) throw new Error(await sessionError(response));
}

async function sessionRequest(path: string, init: RequestInit): Promise<User> {
  const response = await fetch(path, { ...init, credentials: 'same-origin' });
  if (response.status === 401) throw new SessionExpiredError(await sessionError(response));
  if (!response.ok) throw new Error(await sessionError(response));
  const token = (await response.json()) as SessionTokenResponse;
  return new User({
    access_token: token.accessToken,
    id_token: token.idToken,
    token_type: token.tokenType,
    scope: token.scope,
    profile: token.profile,
    expires_at: Math.floor(Date.now() / 1000) + token.expiresIn,
  });
}

async function sessionError(response: Response): Promise<string> {
  try {
    const problem = (await response.json()) as Problem;
    return problem.detail;
  } catch {
    return `Session request failed with status ${response.status}`;
  }
}
